import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Play, Filter, Search, Loader2, AlertCircle, ExternalLink, Link2, Share2,
  Check, Eye, Stethoscope, Activity, Pill, Apple, HeartCrack, MessageCircle,
  HandHeart, Megaphone, RotateCcw, X, Clock, Globe,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import {
  Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, Alert, FormField,
} from '../../components/portal/ui'
import {
  listVideos, getVideoFacets, shareVideo, videoEmbedUrl,
  VIDEO_CATEGORY_KEYS, VIDEO_CATEGORY_LABELS, VIDEO_LANGUAGE_LABELS,
  type Video, type VideoCategory, type VideoFacets,
} from '../../services/videoService'
import { useModalA11y } from '../../components/portal/useModalA11y'

// ================================================================================
// VIDEO LIBRARY — TB awareness videos from the official Central TB Division
// ecosystem (Awareness Videos 2026). Organised by 8 categories with live facet
// counts (deep-linkable via ?category=), a Language filter + search, and a modal
// player that embeds the official YouTube/Drive source. Cards carry an "Official
// source" badge and social-share buttons (which hit the public share counter for
// campaign analytics). No government video content or IDs are fabricated.
// ================================================================================

const CATEGORY_ICON: Record<VideoCategory, typeof Eye> = {
  TB_AWARENESS: Activity,
  SYMPTOMS: Stethoscope,
  TREATMENT: Pill,
  NUTRITION: Apple,
  STIGMA: HeartCrack,
  SURVIVOR_STORIES: MessageCircle,
  NIKSHAY_MITRA: HandHeart,
  COMMUNITY_CAMPAIGNS: Megaphone,
}

const LANG_KEYS = ['all', 'en', 'hi', 'mr', 'multi']
const OFFICIAL_VIDEOS_URL = 'https://tbcindia.mohfw.gov.in/awareness-videos-2026/'
const size = 12

export default function VideosPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()

  const initialCategory = params.get('category') || 'all'
  const [category, setCategory] = useState(initialCategory)
  const [lang, setLang] = useState('all')
  const [q, setQ] = useState('')

  const [items, setItems] = useState<Video[]>([])
  const [facets, setFacets] = useState<VideoFacets | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState<Video | null>(null)

  const categoryLabel = (key: string) =>
    t(`pages.videos.categories.${key}`, { defaultValue: VIDEO_CATEGORY_LABELS[key as VideoCategory] ?? key })
  const langLabel = (key: string) =>
    t(`pages.videos.langs.${key}`, { defaultValue: VIDEO_LANGUAGE_LABELS[key] ?? key })

  // Debounce free-text search.
  const [qDebounced, setQDebounced] = useState('')
  useEffect(() => {
    const td = setTimeout(() => setQDebounced(q), 300)
    return () => clearTimeout(td)
  }, [q])

  // Reflect the category filter in the URL (shareable deep links).
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (category === 'all') next.delete('category')
    else next.set('category', category)
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, fac] = await Promise.all([
        listVideos({
          category: category === 'all' ? undefined : category,
          language: lang === 'all' ? undefined : lang,
          q: qDebounced || undefined,
          page,
          size,
        }),
        getVideoFacets(),
      ])
      setItems(data.content)
      setTotal(data.totalElements)
      setTotalPages(data.totalPages)
      setFacets(fac)
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.videos.error', { defaultValue: 'Could not load videos. Please try again.' }))
    } finally {
      setLoading(false)
    }
  }, [category, lang, qDebounced, page, t])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [category, lang, qDebounced])

  const facetCount = (key: string): number =>
    facets?.categories.find((c) => c.key === key)?.count ?? 0

  const activeFilters = useMemo(
    () =>
      [
        category !== 'all' && `${t('pages.videos.filters.categoryLabel')}: ${categoryLabel(category)}`,
        lang !== 'all' && `${t('pages.videos.filters.langLabel')}: ${langLabel(lang)}`,
        qDebounced && `${t('common.search')}: ${qDebounced}`,
      ].filter((x): x is string => Boolean(x)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category, lang, qDebounced, t]
  )

  const clearFilters = () => { setCategory('all'); setLang('all'); setQ('') }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.videos.seo.title', { defaultValue: 'Videos — TB awareness, symptoms, treatment, nutrition' })}
        description={t('pages.videos.seo.description', { defaultValue: 'Watch official TB awareness videos from the Central TB Division: early detection, symptoms, treatment, nutrition, anti-stigma, Ni-kshay Mitra and community campaigns.' })}
        canonicalPath="/videos"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.videos', 'Videos'), path: '/videos' }]}
      />
      <Section className="bg-gradient-mesh pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.videos', 'Videos') }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.videos.hero.eyebrow', { defaultValue: 'Video library' })}
            title={t('pages.videos.hero.title', { defaultValue: 'TB Awareness Videos' })}
            description={t('pages.videos.hero.description', { defaultValue: 'Official awareness videos from the Central TB Division — early detection, nutrition, anti-stigma and community campaigns. Browse by category.' })}
          />
        </Container>
      </Section>

      {/* 8 category tiles */}
      <Section className="bg-white pt-2">
        <Container>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VIDEO_CATEGORY_KEYS.map((key) => {
              const Icon = CATEGORY_ICON[key]
              const count = facetCount(key)
              const active = category === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(active ? 'all' : key)}
                  aria-pressed={active}
                  className={`text-left rounded-xl border p-4 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(15_76_129)] ${
                    active
                      ? 'border-[rgb(15_76_129)] bg-[rgb(240_252_255)] ring-1 ring-[rgb(15_76_129)]'
                      : 'border-snow-200 bg-white hover:border-[rgb(15_76_129)]/40 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center shrink-0" aria-hidden="true">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <h2 className="font-semibold text-snow-900 text-sm leading-tight">{categoryLabel(key)}</h2>
                      <p className="text-xs text-snow-500 mt-0.5">{t('pages.videos.categoryCount', { count, defaultValue: `${count} videos` })}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-2">
        <Container>
          {/* Filters — Language + search (category is the tile row above) */}
          <Card className="p-5 mb-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField label={t('pages.videos.filters.langLabel', { defaultValue: 'Language' })} htmlFor="vid-lang">
                <select id="vid-lang" value={lang} onChange={(e) => setLang(e.target.value)} className="select">
                  {LANG_KEYS.map((k) => <option key={k} value={k}>{langLabel(k)}</option>)}
                </select>
              </FormField>
              <FormField label={t('pages.videos.filters.searchLabel', { defaultValue: 'Search' })} htmlFor="vid-search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-snow-400" aria-hidden="true" />
                  <input id="vid-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('pages.videos.filters.searchPlaceholder', { defaultValue: 'Search videos…' })} className="input pl-9" />
                </div>
              </FormField>
            </div>
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-snow-100">
                <Filter className="w-4 h-4 text-snow-400" aria-hidden="true" />
                <span className="text-xs text-snow-500">{t('pages.videos.filters.activeFilters', { defaultValue: 'Active filters:' })}</span>
                {activeFilters.map((f) => <Badge key={f} tone="slate">{f}</Badge>)}
                <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-[rgb(15_76_129)] hover:underline ml-1">
                  <RotateCcw className="w-3 h-3" aria-hidden="true" /> {t('pages.videos.filters.clearAll', { defaultValue: 'Clear all' })}
                </button>
              </div>
            )}
          </Card>

          {category !== 'all' && (
            <div className="flex items-center gap-2 mb-4">
              <Badge tone="teal">{categoryLabel(category)}</Badge>
              <button onClick={() => setCategory('all')} className="text-xs text-[rgb(15_76_129)] hover:underline">
                {t('pages.videos.filters.clearCategory', { defaultValue: 'Show all categories' })}
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-snow-500" aria-live="polite">
              {loading ? t('pages.videos.loading', { defaultValue: 'Loading…' }) : t('pages.videos.count', { count: total, defaultValue: `${total} videos` })}
            </p>
          </div>

          {error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm mb-4" role="alert">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16 text-snow-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('pages.videos.loadingVideos', { defaultValue: 'Loading videos…' })}</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Play className="w-7 h-7" />}
              title={t('pages.videos.empty.title', { defaultValue: 'No videos in this category yet' })}
              description={t('pages.videos.empty.desc', { defaultValue: 'Verified official videos for this category will appear here once added. Meanwhile, browse the full official awareness-video library.' })}
              action={
                <a href={OFFICIAL_VIDEOS_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-sm inline-flex items-center gap-1">
                  <ExternalLink className="w-4 h-4" /> {t('pages.videos.empty.browseOfficial', { defaultValue: 'Browse official videos' })}
                </a>
              }
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((v) => (
                <VideoCard
                  key={v.id}
                  video={v}
                  categoryLabel={categoryLabel}
                  langLabel={langLabel}
                  onPlay={() => setPlaying(v)}
                  shareLabel={t('pages.videos.share.label', { defaultValue: 'Share' })}
                  copyLabel={t('pages.videos.share.copy', { defaultValue: 'Copy link' })}
                  copiedLabel={t('pages.videos.share.copied', { defaultValue: 'Copied' })}
                  officialLabel={t('pages.videos.officialSource', { defaultValue: 'Official source' })}
                  watchLabel={t('pages.videos.watch', { defaultValue: 'Watch video' })}
                  openLabel={t('pages.videos.openOfficial', { defaultValue: 'Open official video' })}
                  sourcePrefix={t('pages.videos.sourcePrefix', { defaultValue: 'Source: ' })}
                  featuredLabel={t('pages.videos.featured', { defaultValue: 'Featured' })}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline btn-sm disabled:opacity-50">{t('common.previous')}</button>
              <span className="text-xs text-snow-500">{t('common.pageXofY', { page: page + 1, total: totalPages })}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-outline btn-sm disabled:opacity-50">{t('common.next')}</button>
            </div>
          )}
        </Container>
      </Section>

      {playing && (
        <VideoPlayerModal video={playing} onClose={() => setPlaying(null)} categoryLabel={categoryLabel} />
      )}
    </PortalLayout>
  )
}

// ---------------------------------------------------------------------
// Video card — thumbnail + play button (opens the modal player), an
// "Official source" badge, and social-share buttons.
// ---------------------------------------------------------------------

interface VideoCardProps {
  video: Video
  categoryLabel: (k: string) => string
  langLabel: (k: string) => string
  onPlay: () => void
  shareLabel: string
  copyLabel: string
  copiedLabel: string
  officialLabel: string
  watchLabel: string
  openLabel: string
  sourcePrefix: string
  featuredLabel: string
}

function VideoCard(props: VideoCardProps) {
  const { video: v } = props
  const [copied, setCopied] = useState(false)
  const embed = videoEmbedUrl(v)
  const thumb = v.thumbnailUrl || (v.youtubeId ? `https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg` : '')
  const canPlay = !!embed

  const shareUrl = v.externalUrl || (typeof window !== 'undefined' ? `${window.location.origin}/videos?category=${v.category}` : '')
  const shareText = `${v.title} — TB Mukt Bharat awareness video`

  const trackShare = async (channel: string) => {
    try { await shareVideo(v.id) } catch { /* analytics only */ }
    // eslint-disable-next-line no-console
    console.debug('video share', channel, v.id)
  }

  const openShare = (channel: 'whatsapp' | 'twitter' | 'facebook' | 'linkedin') => {
    const u = encodeURIComponent(shareUrl)
    const txt = encodeURIComponent(shareText)
    const map: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${txt}%20${u}`,
      twitter: `https://twitter.com/intent/tweet?text=${txt}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    }
    trackShare(channel)
    window.open(map[channel], '_blank', 'noopener,noreferrer,width=600,height=540')
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      trackShare('copy')
      setTimeout(() => setCopied(false), 1800)
    } catch { /* clipboard may be unavailable */ }
  }

  return (
    <Card className="p-0 h-full flex flex-col overflow-hidden">
      {/* Thumbnail / play */}
      <div className="relative aspect-video bg-snow-100">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-snow-300"><Play className="w-10 h-10" /></div>
        )}
        {canPlay && (
          <button
            type="button"
            onClick={props.onPlay}
            aria-label={props.watchLabel}
            className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/35 transition group"
          >
            <span className="w-14 h-14 rounded-full bg-white/95 text-[rgb(15_76_129)] flex items-center justify-center shadow-lg group-hover:scale-105 transition min-h-[44px] min-w-[44px]">
              <Play className="w-6 h-6 ml-0.5" fill="currentColor" aria-hidden="true" />
            </span>
            <span className="sr-only">{props.watchLabel}</span>
          </button>
        )}
        {v.duration && (
          <span className="absolute bottom-2 right-2 bg-black/75 text-white text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1">
            <Clock className="w-3 h-3" aria-hidden="true" /> {v.duration}
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start gap-2 mb-2">
          <Badge tone="blue">{props.categoryLabel(v.category)}</Badge>
          {v.isFeatured && <Badge tone="teal">{props.featuredLabel}</Badge>}
        </div>
        <h3 className="font-semibold text-snow-900 line-clamp-2">{v.title}</h3>
        {v.description && <p className="text-sm text-snow-600 line-clamp-3 mt-1 flex-1">{v.description}</p>}

        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {v.language && <Badge tone="slate"><Globe className="w-3 h-3 mr-1 inline" aria-hidden="true" />{props.langLabel(v.language)}</Badge>}
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-0.5">
            <Check className="w-3 h-3" aria-hidden="true" /> {props.officialLabel}
          </span>
        </div>
        {v.sourceOrg && <p className="text-[11px] text-snow-500 mt-2">{props.sourcePrefix}{v.sourceOrg}</p>}

        <div className="mt-4 pt-3 border-t border-snow-100 flex items-center gap-2 flex-wrap">
          {canPlay ? (
            <button onClick={props.onPlay} className="inline-flex items-center gap-1.5 text-sm font-medium text-[rgb(15_76_129)] hover:underline min-h-[44px]">
              <Play className="w-4 h-4" fill="currentColor" aria-hidden="true" /> {props.watchLabel}
            </button>
          ) : v.externalUrl ? (
            <a href={v.externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-[rgb(15_76_129)] hover:underline min-h-[44px]">
              <ExternalLink className="w-4 h-4" aria-hidden="true" /> {props.openLabel}
            </a>
          ) : null}

          <div className="flex items-center gap-1 ml-auto">
            <ShareBtn label="WhatsApp" title="Share on WhatsApp" onClick={() => openShare('whatsapp')}>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.037zm5.323-10.393c-.082-.138-.301-.222-.629-.378-.328-.156-1.939-.957-2.239-1.067-.3-.11-.519-.165-.737.165-.219.329-.848 1.066-1.04 1.284-.191.219-.383.246-.711.082-.328-.164-1.385-.51-2.638-1.627-.975-.868-1.634-1.94-1.826-2.269-.191-.329-.02-.507.144-.671.148-.148.329-.383.493-.574.164-.191.219-.329.328-.548.11-.219.055-.411-.027-.574-.082-.164-.737-1.776-1.01-2.43-.267-.64-.538-.553-.738-.562l-.629-.011c-.219 0-.574.082-.875.411-.301.329-1.149 1.122-1.149 2.733 0 1.611 1.176 3.167 1.34 3.386.164.219 2.309 3.526 5.592 4.944.782.337 1.392.538 1.869.688.785.25 1.5.214 2.064.13.63-.094 1.939-.792 2.213-1.558.274-.766.274-1.422.192-1.558z"/></svg>
            </ShareBtn>
            <ShareBtn label="X / Twitter" title="Share on X" onClick={() => openShare('twitter')}>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </ShareBtn>
            <ShareBtn label="Facebook" title="Share on Facebook" onClick={() => openShare('facebook')}>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </ShareBtn>
            <ShareBtn label="LinkedIn" title="Share on LinkedIn" onClick={() => openShare('linkedin')}>
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </ShareBtn>
            <ShareBtn label={copied ? props.copiedLabel : props.copyLabel} title={props.copyLabel} onClick={copyLink}>
              {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Link2 className="w-4 h-4" aria-hidden="true" />}
            </ShareBtn>
          </div>
        </div>
      </div>
    </Card>
  )
}

function ShareBtn({ label, title, onClick, children }: { label: string; title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-snow-200 text-snow-600 hover:bg-snow-50 hover:text-[rgb(15_76_129)] transition min-h-[44px] min-w-[44px]"
    >
      {children}
      <span className="sr-only">{label}</span>
    </button>
  )
}

// ---------------------------------------------------------------------
// Modal player — embeds the official YouTube (nocookie) / Drive source.
// ---------------------------------------------------------------------

function VideoPlayerModal({ video, onClose, categoryLabel }: { video: Video; onClose: () => void; categoryLabel: (k: string) => string }) {
  const panelRef = useRef<HTMLDivElement>(null)
  useModalA11y(true, onClose, panelRef)
  const embed = videoEmbedUrl(video)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label={video.title}>
      <div ref={panelRef} className="bg-white rounded-xl max-w-3xl w-full max-h-[92vh] overflow-auto">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-snow-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge tone="blue">{categoryLabel(video.category)}</Badge>
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-md px-2 py-0.5">
                <Check className="w-3 h-3" aria-hidden="true" /> Official source
              </span>
            </div>
            <h3 className="font-semibold text-snow-900 line-clamp-2">{video.title}</h3>
          </div>
          <button onClick={onClose} className="text-snow-400 hover:text-snow-700 p-1.5 rounded-md hover:bg-snow-100 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-video bg-black">
          {embed ? (
            <iframe
              src={embed}
              title={video.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/70">
              {video.externalUrl ? (
                <a href={video.externalUrl} target="_blank" rel="noopener noreferrer" className="btn-primary btn-sm inline-flex items-center gap-1">
                  <ExternalLink className="w-4 h-4" /> Open official video
                </a>
              ) : 'No embeddable source'}
            </div>
          )}
        </div>
        {video.description && <p className="text-sm text-snow-600 p-4">{video.description}</p>}
        {video.sourceOrg && <p className="text-[11px] text-snow-500 px-4 pb-4">Source: {video.sourceOrg}</p>}
      </div>
    </div>
  )
}